import { useCallback, useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { useChat } from "../context/ChatContext";
import { pulsesApi } from "../lib/pulses";
import { PollCard } from "../components/pulses/PollCard";
import { MoodCard } from "../components/pulses/MoodCard";
import { TextCard } from "../components/pulses/TextCard";
import { OfferCard } from "../components/pulses/OfferCard";
import { PulseComposer } from "../components/pulses/PulseComposer";
import { AdWalkerModal } from "../components/pulses/AdWalkerModal";
import { Radio, Loader2 } from "lucide-react";

export default function PulsesPage() {
  const { mode, isNegocio } = useTheme();
  const { subscribeToPulses } = useChat();
  const [pulses, setPulses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await pulsesApi.feed(mode);
      setPulses(data);
    } catch {
      /* handled via toast in caller */
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!subscribeToPulses) return undefined;
    return subscribeToPulses((evt) => {
      if (evt.__poll) {
        load();
        return;
      }
      if (evt.type === "pulse.new") {
        if (evt.mode !== mode) return;
        setPulses((prev) => (prev.some((p) => p.id === evt.pulse.id) ? prev : [evt.pulse, ...prev]));
      } else if (evt.type === "pulse.vote") {
        setPulses((prev) =>
          prev.map((p) => (p.id === evt.pulse_id ? { ...p, payload: evt.payload } : p)),
        );
      } else if (evt.type === "pulse.vibra") {
        setPulses((prev) =>
          prev.map((p) =>
            p.id === evt.pulse_id
              ? { ...p, payload: { ...p.payload, vibras_count: evt.vibras_count } }
              : p,
          ),
        );
      } else if (evt.type === "pulse.word") {
        setPulses((prev) =>
          prev.map((p) =>
            p.id === evt.pulse_id ? { ...p, payload: { ...p.payload, words: evt.words } } : p,
          ),
        );
      }
    });
  }, [subscribeToPulses, mode, load]);

  const onLocalUpdate = (updated) => {
    setPulses((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const onCreated = (created) => {
    if (created.mode === mode) setPulses((prev) => [created, ...prev]);
  };

  const renderCard = (p) => {
    if (p.type === "poll") return <PollCard key={p.id} pulse={p} onUpdate={onLocalUpdate} />;
    if (p.type === "mood") return <MoodCard key={p.id} pulse={p} onUpdate={onLocalUpdate} />;
    if (p.type === "text") return <TextCard key={p.id} pulse={p} onUpdate={onLocalUpdate} />;
    if (p.type === "offer") return <OfferCard key={p.id} pulse={p} />;
    return null;
  };

  return (
    <div
      className="max-w-2xl mx-auto px-4 pt-24 pb-28 q-page-enter"
      data-testid="pulses-page"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="q-chip">
            <Radio size={12} strokeWidth={2.4} />
            {isNegocio ? "Modo Negocio" : "Modo General"}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">
            {isNegocio ? (
              <>
                Flash <span className="q-accent-text">Offers</span>
              </>
            ) : (
              <>
                Onda<span className="q-accent-text">Pulse</span>
              </>
            )}
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            {isNegocio
              ? "Ofertas activas de negocios · expiran en 24 h."
              : "Feed de estados interactivos · expiran en 24 h."}
          </p>
        </div>
        <PulseComposer onCreated={onCreated} />
      </div>

      {isNegocio && (
        <div className="mb-4" data-testid="ad-walker-cta">
          <AdWalkerModal />
        </div>
      )}

      {loading ? (
        <div
          className="q-glass rounded-3xl p-6 flex items-center gap-3"
          data-testid="pulses-loading"
        >
          <Loader2 size={16} className="q-accent-text animate-spin" />
          <span className="text-sm text-slate-300">Cargando pulsos…</span>
        </div>
      ) : pulses.length === 0 ? (
        <div className="q-glass rounded-3xl p-6" data-testid="pulses-empty">
          <div className="flex items-start gap-3">
            <div
              className="h-11 w-11 rounded-2xl flex items-center justify-center border border-white/10"
              style={{ background: "var(--accent-soft)" }}
            >
              <Radio size={20} className="q-accent-text" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">
                {isNegocio
                  ? "No hay ofertas activas por ahora."
                  : "No hay pulsos activos por ahora."}
              </h2>
              <p className="text-sm text-slate-300 mt-1">
                {isNegocio
                  ? "¡Publica tu oferta flash!"
                  : "¡Publica tu OndaPulse!"}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="pulses-list">
          {pulses.map(renderCard)}
        </div>
      )}
    </div>
  );
}
